(() => {
  if (window.__avBundleUpsellInit) return;
  window.__avBundleUpsellInit = true;

  const rootPath =
    (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ||
    "/";
  const cartJsonUrl = `${rootPath}cart.js`;
  const ensureJsonEndpoint = (url, fallback) => {
    if (!url) return fallback;
    if (url.includes(".js")) return url;
    return `${url}.js`;
  };
  const cartAddUrl = ensureJsonEndpoint(
    window.routes && window.routes.cart_add_url,
    `${rootPath}cart/add.js`,
  );
  const cartChangeUrl = ensureJsonEndpoint(
    window.routes && window.routes.cart_change_url,
    `${rootPath}cart/change.js`,
  );
  const cartUpdateUrl = ensureJsonEndpoint(
    window.routes && window.routes.cart_update_url,
    `${rootPath}cart/update.js`,
  );

  const DEFAULT_DEBUG = true;

  const hasDebugQuery = () => {
    try {
      return (
        new URLSearchParams(window.location.search).get("upsell_debug") === "1"
      );
    } catch (error) {
      return false;
    }
  };

  const hasDebugStorage = () => {
    try {
      return window.localStorage?.getItem("avUpsellDebug") === "true";
    } catch (error) {
      return false;
    }
  };

  const isDebugEnabled = (upsell) =>
    DEFAULT_DEBUG ||
    window.__avUpsellDebug === true ||
    upsell?.dataset?.upsellDebug === "true" ||
    hasDebugQuery() ||
    hasDebugStorage();

  const debugLog = (upsell, ...args) => {
    if (!isDebugEnabled(upsell)) return;
    console.log("[Bundle Upsell]", ...args);
  };

  const debugWarn = (upsell, ...args) => {
    if (!isDebugEnabled(upsell)) return;
    console.warn("[Bundle Upsell]", ...args);
  };

  const toNumber = (value) => {
    const numberValue = Number(value);
    return Number.isFinite(numberValue) ? numberValue : 0;
  };

  const normalizePlanId = (value) => {
    const id = toNumber(value);
    return id > 0 ? id : 0;
  };

  const HARD_MAX = 6;
  const AUTO_UPSELL_DELAY = 700;

  const defaultUpgradeHandleMap = {
    "avanelle-cognitive-prime": "creatine-gummies-container-active",
    "creatine-gummies-container-active": "creatine-gummies-container-elite",
  };

  const defaultUpgradeQuantityMap = {
    "avanelle-cognitive-prime": { min: 1, max: 1 },
    "creatine-gummies-container-active": { min: 2, max: 2 },
    "creatine-gummies-container-elite": { min: 3, max: 6 },
  };

  window.bundleUpgradeHandleMap =
    window.bundleUpgradeHandleMap || defaultUpgradeHandleMap;
  window.bundleUpgradeQuantityMap =
    window.bundleUpgradeQuantityMap || defaultUpgradeQuantityMap;

  const getIntervalCountForTotal = (total) => {
    if (total >= 3) return 12;
    if (total >= 2) return 8;
    return 4;
  };

  const getPlanIdFromInput = (
    input,
    intervalCount,
    bundleIntervalCount = 0,
  ) => {
    if (!input) return 0;
    const dataset = input.dataset || {};
    const preferredPlanId = normalizePlanId(dataset.upsellSellingPlanId);
    const shouldPreferBundlePlan =
      preferredPlanId &&
      (!bundleIntervalCount || intervalCount === bundleIntervalCount);

    if (shouldPreferBundlePlan) return preferredPlanId;

    let planId = 0;
    if (intervalCount === 12) {
      planId = normalizePlanId(dataset.upsellSellingPlanInterval12);
    } else if (intervalCount === 8) {
      planId = normalizePlanId(dataset.upsellSellingPlanInterval8);
    } else if (intervalCount === 4) {
      planId = normalizePlanId(dataset.upsellSellingPlanInterval4);
    }
    if (!planId) {
      planId = preferredPlanId;
    }
    return planId;
  };

  const getCheckoutButtons = () =>
    Array.from(
      document.querySelectorAll(
        "#CartDrawer-Checkout, button[name=\"checkout\"], input[name=\"checkout\"]",
      ),
    );

  const isDeltaMode = (upsell) =>
    upsell?.dataset?.upsellDelta === "true";

  const getExistingBundleMap = (upsell) => {
    const bundleId = upsell?.dataset?.bundleId;
    if (!bundleId) return {};
    const items = document.querySelectorAll(
      `[data-bundle-id="${bundleId}"]`,
    );
    const map = {};
    items.forEach((item) => {
      if (item.dataset.bundleParent === "true") return;
      const variantId = item.dataset.variantId;
      if (!variantId) return;
      const quantity = toNumber(item.dataset.bundleQuantity);
      if (quantity <= 0) return;
      map[variantId] = (map[variantId] || 0) + quantity;
    });
    return map;
  };

  const getUpsellTotals = (upsell) => {
    const inputs = upsell.querySelectorAll("[data-upsell-qty-input]");
    const existingMap = getExistingBundleMap(upsell);
    let existingTotal = 0;
    Object.values(existingMap).forEach((qty) => {
      existingTotal += toNumber(qty);
    });
    if (!existingTotal) {
      const fallbackTotal = toNumber(upsell?.dataset?.bundleExistingTotal);
      if (fallbackTotal > 0) {
        existingTotal = fallbackTotal;
      }
    }
    let selectedTotal = 0;
    let existingSelectedTotal = 0;
    const counted = new Set();
    inputs.forEach((input) => {
      selectedTotal += toNumber(input.value);
      const variantId = input.dataset.upsellVariantId;
      if (!variantId || counted.has(variantId)) return;
      existingSelectedTotal += toNumber(existingMap[variantId] || 0);
      counted.add(variantId);
    });
    const desiredTotal = isDeltaMode(upsell)
      ? Math.max(0, existingTotal + selectedTotal)
      : Math.max(0, existingTotal + (selectedTotal - existingSelectedTotal));

    return {
      inputs,
      existingMap,
      existingTotal,
      selectedTotal,
      existingSelectedTotal,
      desiredTotal,
    };
  };

  const getUpsellDirtyState = (upsell) => {
    if (!upsell) return false;
    if (upsell.dataset.upsellDirty === "true") return true;
    const totals = getUpsellTotals(upsell);
    const { inputs, existingMap, desiredTotal, selectedTotal } = totals;
    if (!inputs.length) return false;
    if (isDeltaMode(upsell)) {
      return selectedTotal > 0;
    }
    const desiredIntervalCount = getIntervalCountForTotal(desiredTotal);
    const bundleIntervalCount = toNumber(
      upsell.dataset.bundleSellingPlanIntervalCount,
    );
    const bundleId = upsell.dataset.bundleId;
    const planMap = {};
    if (bundleId) {
      const items = document.querySelectorAll(
        `[data-bundle-id="${bundleId}"]`,
      );
      items.forEach((item) => {
        if (item.dataset.bundleParent === "true") return;
        const variantId = item.dataset.variantId;
        if (!variantId) return;
        const quantity = toNumber(item.dataset.bundleQuantity);
        const planId = normalizePlanId(item.dataset.bundleSellingPlan);
        if (!planMap[variantId]) planMap[variantId] = [];
        planMap[variantId].push({ planId, quantity });
      });
    }
    let dirty = false;
    inputs.forEach((input) => {
      const variantId = input.dataset.upsellVariantId;
      if (!variantId) return;
      const existingQty = toNumber(existingMap[variantId] || 0);
      const currentQty = toNumber(input.value);
      if (currentQty !== existingQty) {
        dirty = true;
      }
      const desiredPlanId = getPlanIdFromInput(
        input,
        desiredIntervalCount,
        bundleIntervalCount,
      );
      if (desiredPlanId > 0) {
        const planEntries = planMap[variantId] || [];
        if (
          planEntries.some(
            (entry) =>
              toNumber(entry.quantity) > 0 && entry.planId !== desiredPlanId,
          )
        ) {
          dirty = true;
        }
      }
    });
    return dirty;
  };

  const getUpsellCopy = (total) => {
    const count = Math.min(Math.max(total, 1), HARD_MAX);
    switch (count) {
      case 1:
        return {
          headline: "Add more — save more 🎉",
          subline: "Each extra bottle unlocks a bigger discount 📉",
          helper: "Add 1 more to save more per bottle ➕",
        };
      case 2:
        return {
          headline: "Nice choice — your price just dropped 💚",
          subline: "Add one more to unlock an even better deal ✨",
          helper: "You’re on your way to our best price 🚀",
        };
      case 3:
        return {
          headline: "You’re unlocking serious savings 💰",
          subline: "Keep going — the price keeps improving 📈",
          helper: "Add 1 more to save even more per bottle ➕",
        };
      case 4:
        return {
          headline: "Almost there — bigger savings ahead 🔥",
          subline: "Most customers stop too early 👀",
          helper: "Just 2 more bottles to reach the best price 🏁",
        };
      case 5:
        return {
          headline: "One more to max savings 💸",
          subline: "Add one more bottle to unlock the lowest price ⭐",
          helper: "Best price unlocks at 6 bottles 🎯",
        };
      default:
        return {
          headline: "🎉 You’ve got the best price!",
          subline: "This is the lowest price we offer — no further discounts ✅",
          helper: "You’re saving compared to single bottles 🥳",
        };
    }
  };

  const setCheckoutBlocked = (blocked) => {
    const checkoutButtons = getCheckoutButtons();
    if (!checkoutButtons.length) return;

    checkoutButtons.forEach((checkout) => {
      if (blocked) {
        if (!checkout.dataset.upsellPrevDisabled) {
          checkout.dataset.upsellPrevDisabled = checkout.disabled ? "true" : "false";
        }
        checkout.disabled = true;
        checkout.dataset.upsellBlocked = "true";
        checkout.setAttribute("aria-busy", "true");
        return;
      }

      if (!checkout.dataset.upsellBlocked) return;
      const wasDisabled = checkout.dataset.upsellPrevDisabled === "true";
      checkout.disabled = wasDisabled;
      delete checkout.dataset.upsellPrevDisabled;
      delete checkout.dataset.upsellBlocked;
      checkout.removeAttribute("aria-busy");
    });
  };

  const updateCheckoutBlockState = (root = document) => {
    const upsells = root.querySelectorAll("[data-bundle-upsell]");
    const shouldBlock = Array.from(upsells).some(
      (upsell) =>
        upsell.dataset.upsellCheckoutBlocked === "true" ||
        upsell.dataset.upsellUpgradeLoading === "true",
    );
    setCheckoutBlocked(shouldBlock);
  };

  const setUpsellCheckoutBlocked = (upsell, blocked) => {
    if (!upsell) return;
    if (blocked) {
      upsell.dataset.upsellCheckoutBlocked = "true";
    } else {
      delete upsell.dataset.upsellCheckoutBlocked;
    }
    updateCheckoutBlockState();
  };

  const fetchCart = () =>
    fetch(cartJsonUrl, { credentials: "same-origin" }).then((response) =>
      response.json(),
    );

  const notifyCartRefresh = () => {
    document.dispatchEvent(
      new CustomEvent("cart-drawer:refresh", {
        bubbles: true,
        detail: { focusUpsell: true },
      }),
    );
    try {
      if (typeof publish === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: "bundle-upsell" });
      }
    } catch (error) {
      // Ignore pubsub failures.
    }
  };

  const addItems = (items) =>
    fetch(cartAddUrl, {
      ...fetchConfig("json"),
      body: JSON.stringify({ items }),
    });

  const changeItemByKey = (key, quantity) =>
    fetch(cartChangeUrl, {
      ...fetchConfig("json"),
      body: JSON.stringify({ id: key, quantity }),
    });

  const updateItemsByKey = (updates) =>
    fetch(cartUpdateUrl, {
      ...fetchConfig("json"),
      body: JSON.stringify({ updates }),
    });

  const getUpsellSelectionMap = (upsell, intervalCount = 0) => {
    const inputs = upsell.querySelectorAll("[data-upsell-qty-input]");
    if (!inputs.length) return null;
    const bundleIntervalCount = toNumber(
      upsell.dataset.bundleSellingPlanIntervalCount,
    );
    const map = {};
    inputs.forEach((input) => {
      const variantId = input.dataset.upsellVariantId;
      if (!variantId) return;
      map[variantId] = {
        quantity: toNumber(input.value),
        sellingPlanId: getPlanIdFromInput(
          input,
          intervalCount,
          bundleIntervalCount,
        ),
      };
    });
    return map;
  };

  const syncUpsellInputs = (upsell) => {
    if (!upsell) return;
    const inputs = upsell.querySelectorAll("[data-upsell-qty-input]");
    if (!inputs.length) return;
    if (isDeltaMode(upsell)) {
      inputs.forEach((input) => {
        const minValue = input.min !== "" ? toNumber(input.min) : 0;
        input.value = Math.max(0, minValue);
      });
      upsell.dataset.upsellDirty = "false";
      return;
    }
    const existingMap = getExistingBundleMap(upsell);
    inputs.forEach((input) => {
      const variantId = input.dataset.upsellVariantId;
      if (!variantId) return;
      const minValue = input.min !== "" ? toNumber(input.min) : 0;
      const existingQty = toNumber(existingMap[variantId] || 0);
      input.value = Math.max(minValue, existingQty);
    });
    upsell.dataset.upsellDirty = "false";
  };

  const getUpgradeTierInfo = (upsell) => {
    const bundleHandle = upsell.dataset.bundleHandle || "";
    const handleMap = window.bundleUpgradeHandleMap || {};
    const quantityMap = window.bundleUpgradeQuantityMap || {};
    const upgradeHandle =
      upsell.dataset.upgradeHandle || handleMap[bundleHandle] || "";
    const upgradeParentId = toNumber(upsell.dataset.upgradeParentId);
    const upgradeMinRaw = toNumber(upsell.dataset.upgradeMin);
    const upgradeMaxRaw = toNumber(upsell.dataset.upgradeMax);
    const fallbackQuantities = upgradeHandle
      ? quantityMap[upgradeHandle] || {}
      : {};
    const mappedMin = toNumber(fallbackQuantities.min);
    const mappedMax = toNumber(fallbackQuantities.max);
    const upgradeMin = mappedMin > 0 ? mappedMin : upgradeMinRaw;
    const upgradeMax = mappedMax > 0 ? mappedMax : upgradeMaxRaw;
    const nextHandle = upgradeHandle ? handleMap[upgradeHandle] || "" : "";
    const nextQuantities = nextHandle ? quantityMap[nextHandle] || {} : {};
    const nextMin = toNumber(nextQuantities.min);
    const nextMax = toNumber(nextQuantities.max);

    return {
      bundleHandle,
      upgradeHandle,
      upgradeParentId,
      upgradeMin,
      upgradeMax,
      nextHandle,
      nextMin,
      nextMax,
    };
  };

  const getUpgradeData = (upsell, total = 0) => {
    const {
      bundleHandle,
      upgradeHandle,
      upgradeParentId,
      upgradeMin,
      upgradeMax,
      nextHandle,
      nextMin,
      nextMax,
    } = getUpgradeTierInfo(upsell);
    const useNextTier =
      nextHandle && upgradeMax > 0 && total > upgradeMax;
    const resolvedMin = useNextTier && nextMin > 0 ? nextMin : upgradeMin;
    const resolvedMax = useNextTier ? nextMax : upgradeMax;

    return {
      bundleId: upsell.dataset.bundleId || "",
      bundleHandle,
      upgradeParentId: useNextTier ? 0 : upgradeParentId,
      upgradeHandle: useNextTier ? nextHandle : upgradeHandle,
      upgradeMin: resolvedMin,
      upgradeMax: resolvedMax,
      upgradeSellingPlan: upsell.dataset.upgradeSellingPlan || "",
      hasNextTier: Boolean(nextHandle),
      isNextTier: useNextTier,
    };
  };

  const fetchProductByHandle = (handle) =>
    fetch(`${rootPath}products/${handle}.js`, {
      credentials: "same-origin",
    }).then((response) => response.json());

  const getProductVariantId = (product) => {
    if (!product || !Array.isArray(product.variants)) return 0;
    const availableVariant = product.variants.find(
      (variant) => variant.available,
    );
    return (availableVariant || product.variants[0] || {}).id || 0;
  };

  const getSellingPlanIdForProduct = (
    product,
    desiredPlanId,
    desiredPlanName,
    desiredIntervalCount,
    desiredIntervalUnit,
    bundleIntervalCount = 0,
  ) => {
    if (!product || !Array.isArray(product.selling_plan_groups)) return 0;
    const plans = [];
    product.selling_plan_groups.forEach((group) => {
      (group.selling_plans || []).forEach((plan) => plans.push(plan));
    });

    const matchById = () => {
      const desiredId = Number(desiredPlanId);
      if (desiredId <= 0) return 0;
      const matched = plans.find((plan) => Number(plan.id) === desiredId);
      return matched ? Number(matched.id) : 0;
    };

    const matchByName = () => {
      if (!desiredPlanName) return 0;
      const normalizedName = desiredPlanName.toLowerCase();
      const matched = plans.find(
        (plan) => (plan.name || "").toLowerCase() === normalizedName,
      );
      return matched ? Number(matched.id) : 0;
    };

    const matchByInterval = () => {
      const desiredInterval = toNumber(desiredIntervalCount);
      if (desiredInterval <= 0) return 0;
      const desiredUnit = desiredIntervalUnit
        ? String(desiredIntervalUnit).toLowerCase()
        : "";
      const matched = plans.find((plan) => {
        const policy = plan.delivery_policy || {};
        const count = toNumber(policy.interval_count);
        if (count !== desiredInterval) return false;
        if (!desiredUnit) return true;
        return String(policy.interval || "").toLowerCase() === desiredUnit;
      });
      return matched ? Number(matched.id) : 0;
    };

    const preferredInterval =
      toNumber(bundleIntervalCount) > 0 &&
      toNumber(desiredIntervalCount) > 0 &&
      toNumber(desiredIntervalCount) !== toNumber(bundleIntervalCount);

    if (preferredInterval) {
      const intervalMatch = matchByInterval();
      if (intervalMatch) return intervalMatch;
      const idMatch = matchById();
      if (idMatch) return idMatch;
      const nameMatch = matchByName();
      if (nameMatch) return nameMatch;
    } else {
      const idMatch = matchById();
      if (idMatch) return idMatch;
      const nameMatch = matchByName();
      if (nameMatch) return nameMatch;
      const intervalMatch = matchByInterval();
      if (intervalMatch) return intervalMatch;
    }

    const intervalMatch = matchByInterval();
    if (intervalMatch) return intervalMatch;

    return plans.length > 0 ? Number(plans[0].id) : 0;
  };

  const clearAutoUpgrade = (upsell) => {
    if (upsell && upsell.__autoUpgradeTimer) {
      clearTimeout(upsell.__autoUpgradeTimer);
      upsell.__autoUpgradeTimer = null;
    }
  };

  const canAutoUpgrade = (
    upsell,
    total,
    min,
    maxLimit,
    selectedTotal,
    isDirty,
  ) => {
    if (!upsell || upsell.dataset.upsellAutoUpgrade !== "true") return false;
    if (upsell.dataset.upsellUpgradeLoading === "true") return false;
    const meetsMin = min > 0 ? total >= min : total > 0;
    const withinMax = maxLimit > 0 ? total <= maxLimit : true;
    return meetsMin && withinMax && selectedTotal > 0 && isDirty;
  };

  const scheduleAutoUpgrade = (
    upsell,
    total,
    min,
    maxLimit,
    selectedTotal,
    isDirty,
    trigger,
  ) => {
    if (
      !canAutoUpgrade(
        upsell,
        total,
        min,
        maxLimit,
        selectedTotal,
        isDirty,
      )
    ) {
      clearAutoUpgrade(upsell);
      return;
    }

    clearAutoUpgrade(upsell);
    upsell.__autoUpgradeTimer = setTimeout(() => {
      const totals = getUpsellTotals(upsell);
      const { inputs, selectedTotal: currentSelected, desiredTotal: currentTotal } =
        totals;
      if (!inputs.length) return;
      const currentDirty = getUpsellDirtyState(upsell);
      upsell.dataset.upsellDirty = currentDirty ? "true" : "false";

      if (
        !canAutoUpgrade(
          upsell,
          currentTotal,
          min,
          maxLimit,
          currentSelected,
          currentDirty,
        )
      ) {
        return;
      }

      trigger(upsell);
    }, AUTO_UPSELL_DELAY);
  };

  const updateUpsellState = (upsell) => {
    const inputs = upsell.querySelectorAll("[data-upsell-qty-input]");
    const hasInputs = inputs.length > 0;
    if (!hasInputs) {
      clearAutoUpgrade(upsell);
      setUpsellCheckoutBlocked(upsell, false);
    }

    const tierInfo = getUpgradeTierInfo(upsell);
    const baseMin = tierInfo.upgradeMin;
    const baseMax = tierInfo.upgradeMax;
    const hasNextTier = Boolean(tierInfo.nextHandle);
    const maxLimit = hasNextTier
      ? tierInfo.nextMax > 0
        ? Math.min(tierInfo.nextMax, HARD_MAX)
        : HARD_MAX
      : baseMax > 0
        ? Math.min(baseMax, HARD_MAX)
        : HARD_MAX;
    const inputState = [];

    if (hasInputs) {
      inputs.forEach((input) => {
        const minValue = input.min !== "" ? toNumber(input.min) : 0;
        const maxValue = input.max !== "" ? toNumber(input.max) : 999;
        let value = toNumber(input.value);
        value = Math.min(maxValue, Math.max(minValue, value));
        input.value = value;
        inputState.push({ input, value, minValue, maxValue });
      });
    }

    const totals = hasInputs
      ? getUpsellTotals(upsell)
      : { selectedTotal: 0, desiredTotal: 0 };
    const totalSelected = totals.selectedTotal;
    const total = totals.desiredTotal;
    const isAtMax = maxLimit > 0 && total >= maxLimit;

    debugLog(upsell, "state", {
      bundleId: upsell.dataset.bundleId,
      totalSelected,
      desiredTotal: total,
      existingTotal: totals.existingTotal,
      tier: {
        baseMin,
        baseMax,
        hasNextTier,
        nextMin: tierInfo.nextMin,
        nextMax: tierInfo.nextMax,
      },
      maxLimit,
      bundleIntervalCount: toNumber(
        upsell.dataset.bundleSellingPlanIntervalCount,
      ),
    });

    if (hasInputs) {
      inputState.forEach(({ input, value, minValue, maxValue }) => {
        const wrap = input.closest("[data-upsell-qty]");
        const product = input.closest("[data-upsell-variant]");
        if (product) {
          product.classList.toggle("is-selected", value > 0);
          const count = product.querySelector("[data-upsell-qty-count]");
          if (count) count.textContent = value;
        }
        if (!wrap) return;
        const minus = wrap.querySelector("[data-upsell-qty-minus]");
        const plus = wrap.querySelector("[data-upsell-qty-plus]");
        const addButton = wrap.querySelector("[data-upsell-add]");
        if (minus) minus.disabled = value <= minValue;
        if (plus) plus.disabled = value >= maxValue || isAtMax;
        if (addButton) addButton.disabled = value >= maxValue || isAtMax;
      });
    }

    const countEl = upsell.querySelector("[data-upsell-selected-count]");
    if (countEl) countEl.textContent = total;

    const headlineEl = upsell.querySelector("[data-upsell-headline]");
    const sublineEl = upsell.querySelector("[data-upsell-subline]");
    const selectionHelper = upsell.querySelector(
      "[data-upsell-selection-helper]",
    );
    const progress = upsell.querySelector("[data-upsell-progress]");
    const progressBar = upsell.querySelector("[data-upsell-progress-bar]");
    const useNextTier = hasNextTier && baseMax > 0 && total > baseMax;
    const targetMin = useNextTier && tierInfo.nextMin > 0 ? tierInfo.nextMin : baseMin;
    const targetMax = useNextTier ? tierInfo.nextMax : baseMax;
    const target =
      targetMin > 0
        ? targetMin
        : targetMax > 0
          ? Math.min(targetMax, maxLimit)
          : 0;
    const remaining = target > 0 ? Math.max(0, target - total) : 0;
    const copy = getUpsellCopy(total);
    if (headlineEl) headlineEl.textContent = copy.headline;
    if (sublineEl) sublineEl.textContent = copy.subline;
    const helperText =
      upsell.dataset.upsellUpgradeLoading === "true"
        ? "Updating bundle..."
        : copy.helper;

    if (selectionHelper) {
      selectionHelper.textContent = helperText;
      selectionHelper.classList.toggle(
        "is-complete",
        target > 0 && total >= target,
      );
      selectionHelper.classList.toggle(
        "is-near",
        target > 1 && total === target - 1,
      );
    }

    if (progress && progressBar) {
      const clampedTotal = Math.min(total, HARD_MAX);
      const percentage = HARD_MAX > 0 ? (clampedTotal / HARD_MAX) * 100 : 0;
      progress.hidden = false;
      progress.setAttribute("aria-valuemin", "0");
      progress.setAttribute("aria-valuemax", String(HARD_MAX));
      progress.setAttribute("aria-valuenow", String(clampedTotal));
      progressBar.style.width = `${percentage}%`;
      progress.classList.toggle("is-complete", clampedTotal >= HARD_MAX);
    }

    const cta = upsell.querySelector("[data-bundle-upsell-cta]");
    if (cta) {
      const meetsMin = targetMin > 0 ? total >= targetMin : total > 0;
      const withinMax = maxLimit > 0 ? total <= maxLimit : true;
      const canUpgrade = meetsMin && withinMax;
      cta.disabled = !canUpgrade;
      cta.classList.toggle("is-disabled", !canUpgrade);
    }

    if (hasInputs) {
      const isDirty = getUpsellDirtyState(upsell);
      upsell.dataset.upsellDirty = isDirty ? "true" : "false";
      const shouldBlockCheckout = canAutoUpgrade(
        upsell,
        total,
        targetMin,
        maxLimit,
        totalSelected,
        isDirty,
      );
      setUpsellCheckoutBlocked(upsell, shouldBlockCheckout);
      scheduleAutoUpgrade(
        upsell,
        total,
        targetMin,
        maxLimit,
        totalSelected,
        isDirty,
        runUpgrade,
      );
    }
  };

  const setUpsellOpen = (upsell, isOpen) => {
    const panel = upsell.querySelector("[data-bundle-upsell-panel]");
    const toggle = upsell.querySelector("[data-bundle-upsell-reveal]");
    const card = upsell.querySelector("[data-upsell-card]");
    if (isOpen) {
      upsell.dataset.upsellOpen = "true";
      if (card) card.dataset.upsellOpen = "true";
    } else {
      delete upsell.dataset.upsellOpen;
      if (card) delete card.dataset.upsellOpen;
    }
    if (panel) panel.setAttribute("aria-hidden", isOpen ? "false" : "true");
    if (toggle) toggle.setAttribute("aria-expanded", isOpen ? "true" : "false");
  };

  const initUpsells = (root = document) => {
    const upsells = root.querySelectorAll("[data-bundle-upsell]");
    upsells.forEach((upsell) => {
      if (!upsell.dataset.upsellOpen) {
        setUpsellOpen(upsell, false);
      }
      syncUpsellInputs(upsell);
      bindSlider(upsell);
      updateUpsellState(upsell);
    });
  };

  const bindSlider = (upsell) => {
    if (upsell.dataset.upsellNoSlider === "true") return;
    const slider = upsell.querySelector("[data-bundle-upsell-products]");
    if (!slider || slider.dataset.upsellBound === "true") return;

    slider.dataset.upsellBound = "true";

    const prev = upsell.querySelector("[data-upsell-scroll-prev]");
    const next = upsell.querySelector("[data-upsell-scroll-next]");

    const updateNav = () => {
      if (!prev || !next) return;
      const maxScroll = Math.max(0, slider.scrollWidth - slider.clientWidth - 1);
      prev.disabled = slider.scrollLeft <= 1;
      next.disabled = slider.scrollLeft >= maxScroll;
    };

    const getScrollStep = () => {
      const firstCard = slider.querySelector(".av-cart-upsell__product");
      if (firstCard) {
        const cardWidth = firstCard.getBoundingClientRect().width;
        const styles = window.getComputedStyle(slider);
        const gapValue = parseFloat(styles.columnGap || styles.gap || 0);
        const step = (cardWidth + gapValue) * 2;
        if (Number.isFinite(step) && step > 0) return step;
      }
      return slider.clientWidth || 0;
    };

    const safeScrollBy = (left) => {
      if (typeof slider.scrollBy === "function") {
        try {
          slider.scrollBy({ left, behavior: "smooth" });
          return;
        } catch (error) {
          try {
            slider.scrollBy(left, 0);
            return;
          } catch (err) {
            // Fall through to manual scroll.
          }
        }
      }
      slider.scrollLeft += left;
    };

    const scrollStep = (direction) => {
      const step = getScrollStep();
      if (step <= 0) return;
      safeScrollBy(step * direction);
    };

    if (prev) {
      prev.addEventListener("click", (event) => {
        event.preventDefault();
        scrollStep(-1);
      });
    }

    if (next) {
      next.addEventListener("click", (event) => {
        event.preventDefault();
        scrollStep(1);
      });
    }

    slider.addEventListener("scroll", () => {
      requestAnimationFrame(updateNav);
    });

    window.addEventListener("resize", () => {
      requestAnimationFrame(updateNav);
    });

    slider.__upsellUpdateNav = updateNav;
    updateNav();
  };

  const buildUpgradeItems = (
    bundleItems,
    data,
    selectedItems,
    upgradeMeta = {},
  ) => {
    const bundleId = data.bundleId;
    const upgradeMin = data.upgradeMin;
    const upgradeMax = data.upgradeMax;
    const upgradeSellingPlan = data.upgradeSellingPlan;
    const maxLimit =
      upgradeMax > 0 ? Math.min(upgradeMax, HARD_MAX) : HARD_MAX;

    let upgradeChildren = [];
    const hasSelection = Array.isArray(selectedItems);

    if (hasSelection) {
      const baseItems = bundleItems
        .filter((item) => !(item.properties && item.properties._bundle_parent))
        .map((item) => {
          const variantId = item.variant_id || item.id;
          const quantity = toNumber(item.quantity);
          if (!variantId || quantity <= 0) return null;
          const child = {
            id: variantId,
            quantity,
            properties: {
              _bundle_id: bundleId,
            },
          };
          if (upgradeSellingPlan) {
            child.selling_plan = upgradeSellingPlan;
          }
          return child;
        })
        .filter(Boolean);

      const additions = selectedItems
        .map((item) => {
          const variantId = item.id;
          const quantity = toNumber(item.quantity);
          if (!variantId || quantity <= 0) return null;
          return { id: variantId, quantity };
        })
        .filter(Boolean);

      const merged = {};
      baseItems.forEach((item) => {
        merged[item.id] = { ...item };
      });
      additions.forEach((item) => {
        if (merged[item.id]) {
          merged[item.id].quantity += item.quantity;
        } else {
          merged[item.id] = {
            id: item.id,
            quantity: item.quantity,
            properties: {
              _bundle_id: bundleId,
            },
          };
          if (upgradeSellingPlan) {
            merged[item.id].selling_plan = upgradeSellingPlan;
          }
        }
      });

      upgradeChildren = Object.values(merged).filter(
        (item) => item.quantity > 0,
      );
    } else {
      const childItems = bundleItems.filter(
        (item) => !(item.properties && item.properties._bundle_parent),
      );

      upgradeChildren = childItems
        .map((item) => {
          const variantId = item.variant_id || item.id;
          const quantity = toNumber(item.quantity);
          if (!variantId || quantity <= 0) return null;
          const child = {
            id: variantId,
            quantity,
            properties: {
              _bundle_id: bundleId,
            },
          };
          if (upgradeSellingPlan) {
            child.selling_plan = upgradeSellingPlan;
          }
          return child;
        })
        .filter(Boolean);
    }

    if (upgradeChildren.length === 0) return [];

    let totalQty = upgradeChildren.reduce(
      (acc, item) => acc + toNumber(item.quantity),
      0,
    );

    if (upgradeMin > totalQty) {
      if (hasSelection) return [];
      const missingQty = upgradeMin - totalQty;
      upgradeChildren[0].quantity += missingQty;
      totalQty += missingQty;
    }

    if (maxLimit > 0 && totalQty > maxLimit) {
      let overflow = totalQty - maxLimit;
      for (let i = upgradeChildren.length - 1; i >= 0 && overflow > 0; i -= 1) {
        const deduction = Math.min(upgradeChildren[i].quantity, overflow);
        upgradeChildren[i].quantity -= deduction;
        overflow -= deduction;
      }
      upgradeChildren = upgradeChildren.filter((item) => item.quantity > 0);
    }

    const parentItem = {
      id: data.upgradeParentId,
      quantity: 1,
      properties: {
        _bundle_id: bundleId,
        _bundle_parent: "true",
        _bundle_min: upgradeMin,
        _bundle_max: upgradeMax,
      },
    };
    if (upgradeMeta.nextUpgradeParentId) {
      parentItem.properties._bundle_upgrade_parent_id =
        upgradeMeta.nextUpgradeParentId;
    }
    if (upgradeMeta.nextUpgradeHandle) {
      parentItem.properties._bundle_upgrade_handle =
        upgradeMeta.nextUpgradeHandle;
    }
    if (upgradeMeta.nextUpgradeMin > 0) {
      parentItem.properties._bundle_upgrade_min = upgradeMeta.nextUpgradeMin;
    }
    if (upgradeMeta.nextUpgradeMax > 0) {
      parentItem.properties._bundle_upgrade_max = upgradeMeta.nextUpgradeMax;
    }
    if (upgradeMeta.nextUpgradeTitle) {
      parentItem.properties._bundle_upgrade_title = upgradeMeta.nextUpgradeTitle;
    }
    if (upgradeMeta.nextUpgradeCollection) {
      parentItem.properties._bundle_upgrade_collection =
        upgradeMeta.nextUpgradeCollection;
    }
    if (upgradeMeta.nextUpgradeSellingPlan) {
      parentItem.properties._bundle_upgrade_selling_plan =
        upgradeMeta.nextUpgradeSellingPlan;
    }
    if (upgradeSellingPlan) {
      parentItem.selling_plan = upgradeSellingPlan;
    }

    return [parentItem, ...upgradeChildren];
  };

  const runUpgrade = async (upsell, button = null) => {
    if (!upsell) return;
    if (upsell.dataset.upsellUpgradeLoading === "true") return;
    if (button && button.dataset.bundleUpsellLoading === "true") return;

    const totals = getUpsellTotals(upsell);
    const { inputs, desiredTotal } = totals;
    if (!inputs.length) return;
    const desiredMap = {};
    const inputByVariant = {};
    const bundleIntervalCount = toNumber(
      upsell.dataset.bundleSellingPlanIntervalCount,
    );
    const desiredIntervalCount = getIntervalCountForTotal(desiredTotal);
    const desiredIntervalUnit = (
      upsell.dataset.bundleSellingPlanInterval || "week"
    ).toLowerCase();
    inputs.forEach((input) => {
      const variantId = input.dataset.upsellVariantId;
      if (!variantId) return;
      const qty = Math.max(0, toNumber(input.value));
      const normalizedId = String(variantId);
      inputByVariant[normalizedId] = input;
      desiredMap[normalizedId] = {
        quantity: qty,
        sellingPlanId: getPlanIdFromInput(
          input,
          desiredIntervalCount,
          bundleIntervalCount,
        ),
      };
    });
    let selectedItems = Object.entries(desiredMap)
      .filter(([, entry]) => entry.quantity > 0)
      .map(([id, entry]) => ({
        id,
        quantity: entry.quantity,
        sellingPlanId: entry.sellingPlanId,
      }));
    if (!selectedItems.length) {
      debugWarn(upsell, "runUpgrade: no selected items", {
        desiredMap,
        desiredTotal,
      });
      return;
    }

    debugLog(upsell, "runUpgrade:start", {
      desiredTotal,
      desiredIntervalCount,
      desiredIntervalUnit,
      selectedItems,
    });

    clearAutoUpgrade(upsell);
    upsell.dataset.upsellUpgradeLoading = "true";
    setUpsellCheckoutBlocked(upsell, true);
    if (button) {
      button.dataset.bundleUpsellLoading = "true";
      button.disabled = true;
    }

    let success = false;
    const data = getUpgradeData(upsell, desiredTotal);
    if (!data.bundleId) {
      debugWarn(upsell, "runUpgrade: missing bundleId", data);
      if (button) {
        button.disabled = false;
        button.dataset.bundleUpsellLoading = "false";
      }
      upsell.dataset.upsellUpgradeLoading = "false";
      setUpsellCheckoutBlocked(upsell, false);
      return false;
    }

    try {
      if (data.upgradeHandle) {
        try {
          const product = await fetchProductByHandle(data.upgradeHandle);
          const upgradeSupportsPlans =
            Array.isArray(product.selling_plan_groups) &&
            product.selling_plan_groups.length > 0;
          data.upgradeSupportsPlans = upgradeSupportsPlans;
          if (!data.upgradeParentId) {
            data.upgradeParentId = getProductVariantId(product);
          }
          const desiredPlanId = toNumber(
            upsell.dataset.bundleSellingPlan || data.upgradeSellingPlan,
          );
          const desiredPlanName = upsell.dataset.bundleSellingPlanName || "";
          const resolvedPlanId = getSellingPlanIdForProduct(
            product,
            desiredPlanId,
            desiredPlanName,
            desiredIntervalCount,
            desiredIntervalUnit,
            bundleIntervalCount,
          );
          data.upgradeSellingPlan = upgradeSupportsPlans
            ? normalizePlanId(resolvedPlanId)
            : 0;
        } catch (error) {
          console.error(error);
        }
      }

      if (!data.upgradeParentId) {
        debugWarn(upsell, "runUpgrade: missing upgradeParentId", data);
        return;
      }

      const cart = await fetchCart();
      const bundleItems = cart.items.filter(
        (item) =>
          item.properties &&
          String(item.properties._bundle_id) === String(data.bundleId),
      );

      if (bundleItems.length === 0) {
        debugWarn(upsell, "runUpgrade: no bundle items", {
          bundleId: data.bundleId,
        });
        return;
      }
      const parentItem = bundleItems.find(
        (item) => item.properties && item.properties._bundle_parent,
      );
      const parentProps = parentItem ? parentItem.properties || {} : {};
      const upgradeCollection =
        parentProps._bundle_upgrade_collection || "";
      const upgradeTitle = parentProps._bundle_upgrade_title || "";

      const handleMap = window.bundleUpgradeHandleMap || {};
      const quantityMap = window.bundleUpgradeQuantityMap || {};
      let nextUpgradeHandle = data.upgradeHandle
        ? handleMap[data.upgradeHandle] || ""
        : "";
      let nextUpgradeMin = 0;
      let nextUpgradeMax = 0;
      let nextUpgradeParentId = 0;
      let nextUpgradeSellingPlan = 0;
      let nextUpgradeSupportsPlans = false;

      if (nextUpgradeHandle) {
        const nextQuantities = quantityMap[nextUpgradeHandle] || {};
        nextUpgradeMin = toNumber(nextQuantities.min);
        nextUpgradeMax = toNumber(nextQuantities.max);
        try {
          const product = await fetchProductByHandle(nextUpgradeHandle);
          nextUpgradeSupportsPlans =
            Array.isArray(product.selling_plan_groups) &&
            product.selling_plan_groups.length > 0;
          nextUpgradeParentId = getProductVariantId(product);
          const desiredPlanId = toNumber(
            upsell.dataset.bundleSellingPlan || data.upgradeSellingPlan,
          );
          const desiredPlanName = upsell.dataset.bundleSellingPlanName || "";
          const resolvedPlanId = getSellingPlanIdForProduct(
            product,
            desiredPlanId,
            desiredPlanName,
            desiredIntervalCount,
            desiredIntervalUnit,
            bundleIntervalCount,
          );
          nextUpgradeSellingPlan = nextUpgradeSupportsPlans
            ? normalizePlanId(resolvedPlanId)
            : 0;
        } catch (error) {
          console.error(error);
        }
      } else {
        nextUpgradeHandle = data.upgradeHandle || "";
        nextUpgradeMin = data.upgradeMin;
        nextUpgradeMax = data.upgradeMax;
        nextUpgradeParentId = data.upgradeParentId;
        nextUpgradeSellingPlan = data.upgradeSellingPlan;
      }

      const currentSellingPlan = data.upgradeSupportsPlans
        ? normalizePlanId(data.upgradeSellingPlan)
        : 0;
      const nextSellingPlan = nextUpgradeSupportsPlans
        ? normalizePlanId(nextUpgradeSellingPlan)
        : 0;

      const childItems = bundleItems.filter(
        (item) => !(item.properties && item.properties._bundle_parent),
      );
      const existingVariantMap = {};
      childItems.forEach((item) => {
        const variantId = String(item.variant_id || item.id || "");
        if (!variantId) return;
        const quantity = toNumber(item.quantity);
        if (!existingVariantMap[variantId]) {
          existingVariantMap[variantId] = {
            quantity: 0,
            planId: normalizePlanId(
              item.selling_plan_allocation &&
                item.selling_plan_allocation.selling_plan &&
                item.selling_plan_allocation.selling_plan.id,
            ),
          };
        }
        existingVariantMap[variantId].quantity += quantity;
      });

      if (isDeltaMode(upsell)) {
        const mergedMap = {};
        const variantIds = new Set([
          ...Object.keys(existingVariantMap),
          ...Object.keys(desiredMap),
        ]);
        variantIds.forEach((variantId) => {
          const existingEntry = existingVariantMap[variantId] || {
            quantity: 0,
            planId: 0,
          };
          const desiredEntry = desiredMap[variantId] || {
            quantity: 0,
            sellingPlanId: 0,
          };
          const input = inputByVariant[variantId];
          const planId = input
            ? getPlanIdFromInput(
                input,
                desiredIntervalCount,
                bundleIntervalCount,
              )
            : normalizePlanId(existingEntry.planId);
          mergedMap[variantId] = {
            quantity:
              existingEntry.quantity + toNumber(desiredEntry.quantity),
            sellingPlanId: normalizePlanId(planId),
          };
        });
        Object.keys(desiredMap).forEach((key) => delete desiredMap[key]);
        Object.entries(mergedMap).forEach(([key, entry]) => {
          desiredMap[key] = entry;
        });
        selectedItems = Object.entries(desiredMap)
          .filter(([, entry]) => entry.quantity > 0)
          .map(([id, entry]) => ({
            id,
            quantity: entry.quantity,
            sellingPlanId: entry.sellingPlanId,
          }));
      }
      const grouped = {};
      childItems.forEach((item) => {
        const variantId = String(item.variant_id || item.id || "");
        if (!variantId) return;
        if (!grouped[variantId]) grouped[variantId] = [];
        grouped[variantId].push(item);
      });

      const updates = {};
      Object.entries(grouped).forEach(([variantId, items]) => {
        const desiredEntry = desiredMap[variantId] || {};
        const desiredQty = toNumber(desiredEntry.quantity || 0);
        const desiredPlanId = normalizePlanId(desiredEntry.sellingPlanId);
        let applied = false;

        items.forEach((item) => {
          if (!item.key) return;
          const itemPlanId = normalizePlanId(
            item.selling_plan_allocation &&
              item.selling_plan_allocation.selling_plan &&
              item.selling_plan_allocation.selling_plan.id,
          );
          const planMatches =
            desiredPlanId > 0 ? itemPlanId === desiredPlanId : itemPlanId === 0;

          if (!applied && planMatches) {
            updates[item.key] = desiredQty;
            applied = true;
            return;
          }

          updates[item.key] = 0;
        });

        if (applied || desiredQty === 0) {
          delete desiredMap[variantId];
        } else {
          desiredMap[variantId] = {
            quantity: desiredQty,
            sellingPlanId: desiredPlanId,
          };
        }
      });

      const sameVariant =
        parentItem &&
        toNumber(parentItem.variant_id || parentItem.id) ===
          toNumber(data.upgradeParentId);
      const sameMinMax =
        parentItem &&
        toNumber(parentProps._bundle_min) === toNumber(data.upgradeMin) &&
        toNumber(parentProps._bundle_max) === toNumber(data.upgradeMax);
      const sameUpgradeHandle =
        parentItem &&
        String(parentProps._bundle_upgrade_handle || "") ===
          String(nextUpgradeHandle || "");
      const sameUpgradeParent =
        parentItem &&
        toNumber(parentProps._bundle_upgrade_parent_id) ===
          toNumber(nextUpgradeParentId || 0);
      const sameUpgradeMin =
        parentItem &&
        toNumber(parentProps._bundle_upgrade_min) ===
          toNumber(nextUpgradeMin || 0);
      const sameUpgradeMax =
        parentItem &&
        toNumber(parentProps._bundle_upgrade_max) ===
          toNumber(nextUpgradeMax || 0);
      const sameUpgradeTitle =
        parentItem &&
        String(parentProps._bundle_upgrade_title || "") ===
          String(upgradeTitle || "");
      const sameUpgradeCollection =
        parentItem &&
        String(parentProps._bundle_upgrade_collection || "") ===
          String(upgradeCollection || "");
      const sameUpgradeSellingPlan =
        parentItem &&
        toNumber(parentProps._bundle_upgrade_selling_plan) ===
          toNumber(nextSellingPlan || 0);
      const parentPlanId = normalizePlanId(
        parentItem?.selling_plan_allocation?.selling_plan?.id,
      );
      const sameParentSellingPlan =
        parentPlanId === normalizePlanId(currentSellingPlan);

      const shouldReplaceParent = !(
        parentItem &&
        sameVariant &&
        sameMinMax &&
        sameUpgradeHandle &&
        sameUpgradeParent &&
        sameUpgradeMin &&
        sameUpgradeMax &&
        sameUpgradeTitle &&
        sameUpgradeCollection &&
        sameUpgradeSellingPlan &&
        sameParentSellingPlan
      );

      if (shouldReplaceParent && parentItem && parentItem.key) {
        updates[parentItem.key] = 0;
      }

      if (Object.keys(updates).length > 0) {
        const updateResponse = await updateItemsByKey(updates);
        if (!updateResponse.ok) {
          let updateText = "";
          try {
            updateText = await updateResponse.text();
          } catch (error) {
            updateText = "";
          }
          debugWarn(upsell, "runUpgrade: update failed", {
            status: updateResponse.status,
            body: updateText,
            updates,
          });
          console.error("Bundle update failed", {
            status: updateResponse.status,
            body: updateText,
            updates,
          });
          throw new Error("Bundle update failed");
        }
      }

      const itemsToAdd = [];
      if (shouldReplaceParent || !parentItem) {
        const parentAdd = {
          id: data.upgradeParentId,
          quantity: 1,
          properties: {
            _bundle_id: data.bundleId,
            _bundle_parent: "true",
            _bundle_min: data.upgradeMin,
            _bundle_max: data.upgradeMax,
          },
        };
        if (nextUpgradeParentId) {
          parentAdd.properties._bundle_upgrade_parent_id = nextUpgradeParentId;
        }
        if (nextUpgradeHandle) {
          parentAdd.properties._bundle_upgrade_handle = nextUpgradeHandle;
        }
        if (nextUpgradeMin > 0) {
          parentAdd.properties._bundle_upgrade_min = nextUpgradeMin;
        }
        if (nextUpgradeMax > 0) {
          parentAdd.properties._bundle_upgrade_max = nextUpgradeMax;
        }
        if (upgradeTitle) {
          parentAdd.properties._bundle_upgrade_title = upgradeTitle;
        }
        if (upgradeCollection) {
          parentAdd.properties._bundle_upgrade_collection = upgradeCollection;
        }
        if (nextSellingPlan > 0) {
          parentAdd.properties._bundle_upgrade_selling_plan = nextSellingPlan;
        }
        if (currentSellingPlan > 0) {
          parentAdd.selling_plan = currentSellingPlan;
        }
        itemsToAdd.push(parentAdd);
      }

      Object.entries(desiredMap).forEach(([variantId, entry]) => {
        const qty = toNumber(entry.quantity);
        const numericId = toNumber(variantId);
        if (!numericId || qty <= 0) return;
        const child = {
          id: numericId,
          quantity: qty,
          properties: {
            _bundle_id: data.bundleId,
          },
        };
        const childPlanId = normalizePlanId(entry.sellingPlanId);
        if (childPlanId > 0) {
          child.selling_plan = childPlanId;
        }
        itemsToAdd.push(child);
      });

      if (itemsToAdd.length > 0) {
        const addResponse = await addItems(itemsToAdd);
        if (!addResponse.ok) {
          let errorText = "";
          try {
            errorText = await addResponse.text();
          } catch (error) {
            errorText = "";
          }
          const itemSummary = itemsToAdd.map((item) => ({
            id: item.id,
            qty: item.quantity,
            selling_plan: item.selling_plan || 0,
          }));
          debugWarn(upsell, "runUpgrade: add failed", {
            status: addResponse.status,
            body: errorText,
            items: itemSummary,
          });
          console.error("Bundle add failed", {
            status: addResponse.status,
            body: errorText,
            items: itemsToAdd,
          });
          throw new Error("Bundle add failed");
        }
      }

      success = true;
      debugLog(upsell, "runUpgrade:success", {
        desiredTotal,
        selectedItems,
        itemsToAdd: itemsToAdd.length,
      });
      notifyCartRefresh();
    } catch (error) {
      console.error(error);
    } finally {
      if (button) {
        button.disabled = false;
        button.dataset.bundleUpsellLoading = "false";
      }
      upsell.dataset.upsellUpgradeLoading = "false";
      upsell.dataset.upsellDirty = "false";
      setUpsellCheckoutBlocked(upsell, false);
    }
    return success;
  };

  document.addEventListener("click", async (event) => {
    const button = event.target.closest("[data-bundle-upsell-cta]");
    if (!button) return;

    const upsell = button.closest("[data-bundle-upsell]");
    if (!upsell) return;

    event.preventDefault();
    runUpgrade(upsell, button);
  });

  document.addEventListener("click", async (event) => {
    const checkoutButton = event.target.closest(
      "#CartDrawer-Checkout, button[name=\"checkout\"], input[name=\"checkout\"]",
    );
    if (!checkoutButton) return;

    const upsell = document.querySelector("[data-bundle-upsell]");
    if (!upsell) return;

    if (upsell.dataset.upsellUpgradeLoading === "true") {
      event.preventDefault();
      return;
    }

    const hasSelection = Array.from(
      upsell.querySelectorAll("[data-upsell-qty-input]"),
    ).some((input) => toNumber(input.value) > 0);
    const isDirty =
      getUpsellDirtyState(upsell) ||
      upsell.dataset.upsellDirty === "true" ||
      hasSelection;
    if (!isDirty) return;

    event.preventDefault();
    const upgraded = await runUpgrade(upsell);
    if (!upgraded) return;

    // Keep user in drawer; they can press checkout again after cart refresh.
    requestAnimationFrame(() => {
      if (checkoutButton) checkoutButton.focus();
    });
    return;
  });

  document.addEventListener("click", (event) => {
    const toggle = event.target.closest("[data-bundle-upsell-reveal]");
    if (toggle) {
      const upsell = toggle.closest("[data-bundle-upsell]");
      if (!upsell) return;
      event.preventDefault();
      setUpsellOpen(upsell, true);
      updateUpsellState(upsell);
      const slider = upsell.querySelector("[data-bundle-upsell-products]");
      if (slider && typeof slider.__upsellUpdateNav === "function") {
        requestAnimationFrame(() => slider.__upsellUpdateNav());
      }
      return;
    }

    const addButton = event.target.closest("[data-upsell-add]");
    const plusButton = event.target.closest("[data-upsell-qty-plus]");
    const minusButton = event.target.closest("[data-upsell-qty-minus]");
    if (!addButton && !plusButton && !minusButton) return;

    const upsell = event.target.closest("[data-bundle-upsell]");
    if (!upsell) return;

    const qtyWrap = event.target.closest("[data-upsell-qty]");
    if (!qtyWrap) return;

    const input = qtyWrap.querySelector("[data-upsell-qty-input]");
    if (!input) return;

    const minValue = input.min !== "" ? toNumber(input.min) : 0;
    const maxValue = input.max !== "" ? toNumber(input.max) : 999;
    let value = toNumber(input.value);

    value = addButton || plusButton ? value + 1 : value - 1;
    value = Math.min(maxValue, Math.max(minValue, value));
    input.value = value;
    upsell.dataset.upsellDirty = "true";

    updateUpsellState(upsell);
  });

  document.addEventListener("change", (event) => {
    if (!event.target.matches("[data-upsell-qty-input]")) return;
    const upsell = event.target.closest("[data-bundle-upsell]");
    if (!upsell) return;
    upsell.dataset.upsellDirty = "true";
    updateUpsellState(upsell);
  });

  document.addEventListener("cart-drawer:refresh", () => {
    requestAnimationFrame(() => initUpsells());
  });

  initUpsells();
})();
